/**
 * S-34 Report flow (modal, reachable from every user surface) + S-35 Block
 * confirmation — V8-owned, exported for every vertical to import:
 *
 *   import { ReportSheet, BlockConfirmSheet } from "../safety/report-sheet.js";
 *
 * Dead-serious surfaces: no announcer, no jokes (copy-tone §1.4). Report
 * submissions must never be lost: a failed submit is kept locally and retried.
 */
import { useState } from "react";
import { copy, resolveCopy } from "@humble/shared";
import { api, isApiError } from "../../lib/api.js";
import { Button, Sheet, toast } from "../../design/index.js";

export const REPORT_CATEGORIES_UI: Array<{ value: string; label: string }> = [
  { value: "harassment", label: "Harassment" },
  { value: "hate", label: "Hate speech" },
  { value: "impersonation", label: "Impersonation" },
  { value: "underage_suspicion", label: "Possibly under 18" },
  { value: "nsfw", label: "Nudity / NSFW" },
  { value: "scam_spam", label: "Scam or spam" },
  { value: "violence_threat", label: "Threat of violence" },
  { value: "off_platform_harm", label: "Off-platform harm" },
  { value: "other", label: "Something else" },
];

/** Exactly one of these identifies the report subject (identity-free recourse). */
export interface ReportSubject {
  userId?: string;
  srId?: string;
  sessionId?: string;
  messageId?: string;
  /** Display name when known (never known for srId — that's the point). */
  displayName?: string;
}

export interface ReportSheetProps {
  open: boolean;
  onClose: () => void;
  subject: ReportSubject;
}

export function ReportSheet({ open, onClose, subject }: ReportSheetProps) {
  const [category, setCategory] = useState<string | null>(null);
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setCategory(null);
    setFreeText("");
    setDone(false);
    onClose();
  };

  const submit = async () => {
    if (!category) return;
    setBusy(true);
    try {
      await api.post("/reports", {
        body: {
          category: category as never,
          ...(freeText.trim() ? { free_text: freeText.trim() } : {}),
          ...(subject.userId ? { subject_user_id: subject.userId } : {}),
          ...(subject.srId ? { sr_id: subject.srId } : {}),
          ...(subject.sessionId ? { session_id: subject.sessionId } : {}),
          ...(subject.messageId ? { message_id: subject.messageId } : {}),
        } as never,
      });
      setDone(true);
    } catch (err) {
      // Reports must never be lost — retry guidance, keep the draft.
      toast(isApiError(err) ? err.message : "Couldn't send the report — please retry.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={reset} title={done ? "Report received" : "Report"}>
      {done ? (
        <div>
          <p>{copy.safety.report_ack}</p>
          <Button variant="primary" block onClick={reset}>
            Close
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {subject.displayName && (
            <p className="text-dim" style={{ margin: 0 }}>
              Reporting {subject.displayName}
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {REPORT_CATEGORIES_UI.map((c) => (
              <Button
                key={c.value}
                variant={category === c.value ? "primary" : "ghost"}
                onClick={() => setCategory(c.value)}
                aria-pressed={category === c.value}
              >
                {c.label}
              </Button>
            ))}
          </div>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="Anything our team should know (optional, encouraged)"
            style={{ width: "100%", resize: "vertical" }}
          />
          <p className="text-faint" style={{ fontSize: "0.85rem", margin: 0 }}>
            Submitting attaches an evidence snapshot: the reported profile, relevant messages
            (including deleted ones), and media references. Safety reports are reviewed within 24
            hours. The reported person is not told who reported them.
          </p>
          <Button variant="danger" block loading={busy} disabled={!category} onClick={submit}>
            Submit report
          </Button>
        </div>
      )}
    </Sheet>
  );
}

export interface BlockConfirmSheetProps {
  open: boolean;
  onClose: () => void;
  /** Exactly one of userId | srId | sessionId. */
  target: { userId?: string; srId?: string; sessionId?: string; displayName?: string };
  onBlocked?: () => void;
}

/** S-35 — one confirm step, then the instant SM-11 cascade. */
export function BlockConfirmSheet({ open, onClose, target, onBlocked }: BlockConfirmSheetProps) {
  const [busy, setBusy] = useState(false);
  const anonymous = Boolean(target.srId);
  const confirmCopy = anonymous
    ? copy.safety.block_anonymous_confirm
    : (resolveCopy("safety.block_confirm") ?? "").replace("{name}", target.displayName ?? "them");

  const doBlock = async () => {
    setBusy(true);
    try {
      await api.post("/blocks", {
        body: {
          ...(target.userId ? { user_id: target.userId } : {}),
          ...(target.srId ? { sr_id: target.srId } : {}),
          ...(target.sessionId ? { session_id: target.sessionId } : {}),
        } as never,
      });
      toast("Blocked.", "success");
      onBlocked?.();
      onClose();
    } catch (err) {
      toast(isApiError(err) ? err.message : "Block failed — please retry.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Block">
      <p>{confirmCopy}</p>
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="danger" block loading={busy} onClick={doBlock}>
          Block
        </Button>
        <Button variant="ghost" block onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Sheet>
  );
}
